/**
 * AdminOrdersDashboard.jsx — Dashboard admin « Commandes Entreprise »
 *
 * Liste consolidée des commandes actives (Vague 3), avec leurs
 * paniers de sessions, collabs, budget, consommation et accès direct
 * à la fiche Vague 5 (Consommation & Facturation).
 *
 * API : GET /api/admin/enterprise/orders
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';
import { toast } from 'sonner';
import {
  ShoppingCart, Building2, Filter, Search, ChevronRight,
  Activity, FileText, Receipt, CheckCircle2, Clock, XCircle,
  ArrowLeft, Trash2,
} from 'lucide-react';

const API = getApiUrl();

const ORDER_STATUS = {
  pending_setup: { label: 'Configuration', color: '#D97706', bg: '#FEF3C7', icon: Clock },
  active: { label: 'Active', color: '#047857', bg: '#D1FAE5', icon: Activity },
  completed: { label: 'Terminée', color: '#065F46', bg: '#A7F3D0', icon: CheckCircle2 },
  cancelled: { label: 'Annulée', color: '#991B1B', bg: '#FEE2E2', icon: XCircle },
};

export default function AdminOrdersDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', type: '', q: '' });

  useEffect(() => { fetchItems(); }, [filters]); // eslint-disable-line

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await fetch(`${API}/admin/enterprise/orders?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erreur chargement commandes');
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  const stats = useMemo(() => ({
    total: items.length,
    active: items.filter(i => i.status === 'active').length,
    pending: items.filter(i => i.status === 'pending_setup').length,
    completed: items.filter(i => i.status === 'completed').length,
  }), [items]);

  return (
    <div className="theme-light min-h-screen bg-[#FBF9F3] pb-20" data-testid="admin-orders-dashboard">
      <div className="border-b border-[#E8E5DE] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <button onClick={() => navigate('/admin')}
            data-testid="admin-orders-back"
            className="inline-flex items-center gap-1.5 text-[#4B5563] hover:text-[#111827] text-sm mb-4 px-3 py-1.5 rounded-full bg-[#F8F7F4] border border-[#E8E5DE] hover:bg-[#EBEAE3] transition">
            <ArrowLeft className="w-4 h-4" /> Retour au dashboard
          </button>
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <p className="uppercase text-[11px] tracking-[0.25em] text-[#92710A] font-medium">
              Administration — Entreprise
            </p>
            <Link to="/admin/enterprise-quotes"
              className="text-[11px] text-[#4B5563] hover:text-[#92710A] underline"
              data-testid="admin-orders-goto-quotes">
              → Devis & leads
            </Link>
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-medium text-[#111827] tracking-tight">
            Commandes & livraison
          </h1>
          <p className="text-sm text-[#4B5563] mt-1">
            Suivi opérationnel du catalogue B2B : panier de sessions, collaborateurs, coachs, consommation et facturation.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <KpiCard label="Total commandes" value={stats.total} accent="#111827" />
            <KpiCard label="Actives" value={stats.active} accent="#047857" />
            <KpiCard label="En configuration" value={stats.pending} accent="#D97706" />
            <KpiCard label="Terminées" value={stats.completed} accent="#065F46" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mt-5">
            <div className="relative sm:col-span-2">
              <Search className="w-4 h-4 text-[#6B7280] absolute left-3 top-1/2 -translate-y-1/2" />
              <input placeholder="Rechercher entreprise, devis, id…"
                data-testid="admin-orders-search"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                className="w-full pl-9 pr-3 py-2 rounded-full bg-[#FBF9F3] border border-[#E8E5DE] text-sm focus:outline-none focus:ring-1 focus:ring-[#92710A]" />
            </div>
            <Select data-testid="admin-orders-status-filter"
              value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })}
              placeholder="Statut" options={[
                { value: '', label: 'Tous statuts' },
                { value: 'pending_setup', label: 'Configuration' },
                { value: 'active', label: 'Active' },
                { value: 'completed', label: 'Terminée' },
                { value: 'cancelled', label: 'Annulée' },
              ]} />
            <Select data-testid="admin-orders-type-filter"
              value={filters.type} onChange={(v) => setFilters({ ...filters, type: v })}
              placeholder="Type" options={[
                { value: '', label: 'Tous types' },
                { value: 'ia', label: 'Coaching IA' },
                { value: 'human', label: 'Coaching Humain' },
              ]} />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {loading ? (
          <div className="text-sm text-[#6B7280] py-12 text-center">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-[#6B7280] py-12 text-center bg-white border border-[#E8E5DE] rounded-2xl">
            <ShoppingCart className="w-10 h-10 mx-auto text-[#D6D3D1] mb-2" />
            Aucune commande avec ces filtres.
            <br />
            <Link to="/admin/enterprise-quotes" className="text-[#92710A] underline text-xs mt-2 inline-block">
              Voir les devis en cours
            </Link>
          </div>
        ) : (
          <div className="space-y-2" data-testid="admin-orders-list">
            {items.map((o, idx) => (
              <OrderRow key={o.id} order={o} idx={idx} token={token}
                onDeleted={() => setItems(cur => cur.filter(x => x.id !== o.id))} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderRow({ order, idx, token, onDeleted }) {
  const st = ORDER_STATUS[order.status] || { label: order.status, color: '#6B7280', bg: '#F3F4F6', icon: Clock };
  const Icon = st.icon;
  const snap = order.company_snapshot || {};
  const b = order.basket;
  const consumedPct = b && b.total_sessions > 0
    ? Math.round((b.consumed_sessions / b.total_sessions) * 100) : 0;

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const company = snap.company_name || order.quote_number || order.id;
    const consumed = b?.consumed_sessions || 0;
    let confirmMsg =
      `Supprimer la commande "${company}" ?\n\n` +
      `• Suppression logique (soft delete)\n` +
      `• La commande ne sera plus visible dans la liste\n` +
      `• Les collaborateurs et le panier sont conservés\n` +
      `• Action tracée dans l'audit log admin`;
    if (consumed > 0) {
      confirmMsg += `\n\n⚠️ ${consumed} session(s) déjà consommée(s). Cette action sera forcée (--force).`;
    }
    if (!window.confirm(confirmMsg)) return;
    try {
      const url = `${getApiUrl()}/admin/enterprise/orders/${order.id}${consumed > 0 ? '?force=true' : ''}`;
      const res = await fetch(url, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const d = err.detail;
        const msg = (d && typeof d === 'object') ? (d.message || d.error_code) : (d || err.message || `HTTP ${res.status}`);
        throw new Error(msg);
      }
      toast.success('Commande supprimée');
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(`Échec : ${err.message}`);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.02 }}
      className="relative group">
      <Link to={`/admin/enterprise-orders/${order.id}`}
        data-testid={`admin-order-row-${order.id}`}
        className="block bg-white border border-[#E8E5DE] rounded-2xl p-4 pr-14 hover:border-[#92710A] transition">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2 flex-wrap">
              <Building2 className="w-4 h-4 text-[#92710A]" />
              <span className="text-sm font-display font-medium text-[#111827]">
                {snap.company_name || '—'}
              </span>
              <span className="text-[10px] uppercase font-medium text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded-full">
                {order.type === 'ia' ? 'IA' : 'Humain'}
              </span>
              <span className="text-[10px] uppercase font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                style={{ color: st.color, backgroundColor: st.bg }}>
                <Icon className="w-3 h-3" /> {st.label}
              </span>
              <span className="text-[10px] uppercase font-medium text-[#4B5563] bg-[#FBF9F3] border border-[#E8DFC8] px-2 py-0.5 rounded-full">
                {order.billing_mode === 'forfait' ? 'Forfait' : 'Consommation'}
              </span>
            </div>
            <div className="text-[11px] text-[#6B7280] mt-1 font-mono">
              Devis {order.quote_number}
            </div>
          </div>

          <div className="flex items-center gap-6 flex-wrap">
            {b && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">Sessions</div>
                <div className="text-sm text-[#111827]">
                  <b>{b.consumed_sessions}</b>
                  <span className="text-[#6B7280]"> / {b.total_sessions}</span>
                </div>
                <div className="w-28 h-1.5 rounded-full bg-[#F3F4F6] mt-1 overflow-hidden">
                  <div className="h-full bg-[#92710A]" style={{ width: `${consumedPct}%` }} />
                </div>
              </div>
            )}
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">Budget TTC</div>
              <div className="text-sm text-[#111827]">
                {order.total_budget_ttc?.toLocaleString('fr-FR')} €
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">Collabs</div>
              <div className="text-sm text-[#111827]">{order.collaborators_count ?? 0}</div>
            </div>

            <div className="flex items-center gap-2 text-[#92710A]">
              <Receipt className="w-4 h-4" />
              <FileText className="w-4 h-4" />
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </Link>
      <button onClick={handleDelete}
        data-testid={`delete-order-${order.id}`}
        title="Supprimer la commande"
        aria-label="Supprimer la commande"
        className="absolute top-2 right-2 z-10 p-2 rounded-lg bg-white border border-[#FECACA] text-[#991B1B] shadow-sm hover:bg-[#FEE2E2] hover:border-[#EF4444] active:scale-95 transition-all min-w-[40px] min-h-[40px] flex items-center justify-center">
        <Trash2 className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

function KpiCard({ label, value, accent = '#111827' }) {
  return (
    <div className="p-3 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8]">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">{label}</div>
      <div className="text-2xl font-display mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Select({ value, onChange, options, placeholder, ...props }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2 rounded-full bg-[#FBF9F3] border border-[#E8E5DE] text-sm focus:outline-none focus:ring-1 focus:ring-[#92710A]"
      {...props}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label || placeholder}</option>)}
    </select>
  );
}
